import { Link, useLocation } from "react-router-dom";
import Top from "./Top";
import { useEffect, useRef, useState } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import GLightbox from "glightbox";
import { toast } from "react-toastify";
import { getUserFromToken } from "../../function";
import { server } from "../../server";

const BIN_API =
  "https://b2i4nr8bza.execute-api.ap-south-1.amazonaws.com/Prod/Bin";

function Bin() {
  const location = useLocation();
  const { event } = location.state || {};
  const user = getUserFromToken();
  const token = localStorage.getItem("authToken");

  // ── Images ────────────────────────────────────────────────────────────────
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalImages, setTotalImages] = useState(0);

  // ── Interaction ───────────────────────────────────────────────────────────
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [actionMode, setActionMode] = useState("restore"); // "restore" | "delete"

  // ── Fetch bin images ──────────────────────────────────────────────────────
  const fetchBinImages = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        userObjectId: user?._id,
        ...(event?.eventId ? { eventId: event.eventId } : {}),
      });
      const res = await fetch(`${BIN_API}/GetBinImages?${params.toString()}`, {
        headers: { Authorization: token },
      });
      const data = await res.json();
      const imgs = data?.images || [];
      setImages(imgs);
      setTotalImages(imgs.length);
    } catch (err) {
      console.error(err);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBinImages();
  }, []);

  // ── GLightbox ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const lb = GLightbox({ selector: ".glightbox-bin" });
    return () => lb.destroy();
  }, [images, isSelectMode]); // ← add isSelectMode

  // ── Filtering ─────────────────────────────────────────────────────────────
  const filteredImages = images.filter((img) => {
    const fileName =
      img?.key
        ?.split("/")
        ?.pop()
        ?.replace(/^[^_]+_/, "") || "";
    return fileName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // ── Select helpers ────────────────────────────────────────────────────────
  const toggleSelectMode = () => {
    setIsSelectMode((v) => !v);
    if (isSelectMode) setSelectedImages([]);
  };
  const toggleImageSelect = (id) =>
    setSelectedImages((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id],
    );
  const toggleSelectAll = () =>
    setSelectedImages(
      selectedImages.length === images.length ? [] : images.map((i) => i._id),
    );

  // ── Helper: get ids + their eventIds from the images array ───────────────
  // The API requires eventId per call. Since binned images can come from
  // different events, we group them by eventId and fire one request per group.
  const buildRestoreGroups = (ids) => {
    // ids → map to their image objects → group by eventId
    const groups = {};
    ids.forEach((id) => {
      const img = images.find((i) => i._id === id);
      const eid = img?.eventId || event?.eventId;
      if (!eid) return; // skip if we somehow have no eventId
      if (!groups[eid]) groups[eid] = [];
      groups[eid].push(id);
    });
    return groups; // { eventId: [imageId, ...], ... }
  };

  // ── Restore ───────────────────────────────────────────────────────────────
  const handleRestore = async () => {
    setRestoreLoading(true);
    try {
      const ids =
        selectedImages.length > 0 ? selectedImages : [selectedImageId];
      if (!ids.length) return;

      const groups = buildRestoreGroups(ids);

      await Promise.all(
        Object.entries(groups).map(([eventId, imageIds]) =>
          fetch(`${BIN_API}/MoveToTrashOrRestore`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({
              action: "restore",
              userObjectId: user?._id,
              eventId,
              imageIds,
            }),
          }),
        ),
      );

      document.querySelector("#binActionModal .btn-close-modal")?.click();
      setSelectedImageId(null);
      setSelectedImages([]);
      setIsSelectMode(false);
      toast.success(
        `${ids.length} photo${ids.length > 1 ? "s" : ""} restored`,
        { position: "bottom-center", autoClose: 2000 },
      );
      fetchBinImages();
    } catch (err) {
      console.error(err);
      toast.error("Failed to restore.", { position: "bottom-center" });
    } finally {
      setRestoreLoading(false);
    }
  };

  // ── Permanent Delete ──────────────────────────────────────────────────────
  const handlePermanentDelete = async () => {
    setDeleteLoading(true);
    try {
      const ids =
        selectedImages.length > 0 ? selectedImages : [selectedImageId];
      if (!ids.length) return;

      await fetch(`${server}/Images/DeleteImages`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ imageIds: ids }),
      });

      document.querySelector("#binActionModal .btn-close-modal")?.click();
      setSelectedImageId(null);
      setSelectedImages([]);
      setIsSelectMode(false);
      toast.success(
        `${ids.length} photo${ids.length > 1 ? "s" : ""} permanently deleted`,
        { position: "bottom-center", autoClose: 2000 },
      );
      fetchBinImages();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete.", { position: "bottom-center" });
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Download ──────────────────────────────────────────────────────────────
  const handleSingleDownload = async (imgUrl, fileName = "image.jpg") => {
    try {
      const blob = await (await fetch(imgUrl, { mode: "cors" })).blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  const handleMultiDownload = async () => {
    if (!selectedImages.length) return;
    const zip = new JSZip();
    const folder = zip.folder("images");
    const sel = images.filter((img) => selectedImages.includes(img._id));
    for (const [i, img] of sel.entries()) {
      const blob = await (await fetch(img.imageUrl)).blob();
      folder.file(`image_${i + 1}.jpg`, blob);
    }
    saveAs(await zip.generateAsync({ type: "blob" }), "bin_images.zip");
  };

  // ── Open modal helper ─────────────────────────────────────────────────────
  const openModal = (mode, imgId = null) => {
    setActionMode(mode);
    if (imgId) setSelectedImageId(imgId);
  };

  const modalTitle =
    actionMode === "restore"
      ? selectedImages.length > 1
        ? "Restore Photos"
        : "Restore Photo"
      : "Delete Forever";

  const modalBody =
    actionMode === "restore"
      ? selectedImages.length > 1
        ? `Restore ${selectedImages.length} photos back to the event gallery?`
        : "Restore this photo back to the event gallery?"
      : selectedImages.length > 1
        ? `Permanently delete ${selectedImages.length} photos? This cannot be undone.`
        : "Permanently delete this photo? This cannot be undone.";

  const modalSubtext =
    actionMode === "restore"
      ? "Photos will be available in the gallery again."
      : "Once deleted, these photos are gone forever.";

  const isActionLoading =
    actionMode === "restore" ? restoreLoading : deleteLoading;
  const handleAction =
    actionMode === "restore" ? handleRestore : handlePermanentDelete;

  return (
    <>
      <main>
        <Top />
        <div className="container">
          <div className="row g-3">
            <div className="col-12 py-2">
              {/* ── Page Header ── */}
              <section className="position-relative z-1">
                <div className="pb-3 d-flex flex-column flex-md-row gap-0 align-items-md-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <Link
                      to="/all-events"
                      state={{ event }}
                      className="action-button-md bg-white-transparent rounded-circle"
                      onClick={(e) => {
                        e.preventDefault();
                        window.history.back();
                      }}
                    >
                      <i className="bi bi-arrow-left fs-4 text-dark"></i>
                    </Link>

                    <div className="ms-3">
                      <h1 className="mb-0 h4 text-light lh-1 fw-medium">
                        Bin
                        {event?.name && (
                          <span className="text-light fs-6 ms-2 opacity-60 fw-normal">
                            — {event.name}
                          </span>
                        )}
                        <span className="text-light fs-6 ms-1 opacity-75">
                          ({totalImages}{" "}
                          {totalImages === 1 ? "Photo" : "Photos"})
                        </span>
                      </h1>
                      <nav aria-label="breadcrumb" className="m-0">
                        <ol className="breadcrumb">
                          <li className="breadcrumb-item">
                            <span>Home</span>
                          </li>
                          <li className="breadcrumb-item">
                            <span>All Events</span>
                          </li>
                          {event?.name && (
                            <li className="breadcrumb-item">
                              <span
                                className="cursor-pointer text-light opacity-75"
                                onClick={() => window.history.back()}
                              >
                                {event.name}
                              </span>
                            </li>
                          )}
                          <li
                            className="breadcrumb-item active"
                            aria-current="page"
                          >
                            Bin
                          </li>
                        </ol>
                      </nav>
                    </div>

                    {/* Desktop multi-select toggle */}
                    <div className="d-lg-flex d-none align-items-center ms-8 gap-2">
                      {!isSelectMode && images.length > 0 && (
                        <p
                          className="m-0 cursor-pointer fs-btn text-light"
                          onClick={toggleSelectMode}
                        >
                          <i className="bi bi-check2-circle me-1 text-light"></i>{" "}
                          MultiSelect
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 mt-lg-0 mt-3 flex-wrap">
                    {/* Mobile multi-select toggle */}
                    <div className="d-lg-none d-flex align-items-center gap-2">
                      {!isSelectMode && images.length > 0 && (
                        <p
                          className="m-0 cursor-pointer fs-btn text-light"
                          onClick={toggleSelectMode}
                        >
                          <i className="bi bi-check2-circle me-1 text-light"></i>{" "}
                          MultiSelect
                        </p>
                      )}
                    </div>

                    <div className="d-flex align-items-center">
                      <span className="position-absolute ps-3 search-icon">
                        <i className="bi bi-search ms-1"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control px-7 normal-text border-0 bg-card-dark rounded-pill"
                        placeholder="Search Image"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* ── Info strip ── */}
              {!loading && images.length > 0 && (
                <div
                  className="rounded-4 px-4 py-3 mb-3 d-flex align-items-center gap-2"
                  style={{
                    background: "rgba(220,53,69,0.10)",
                    border: "1px solid rgba(220,53,69,0.2)",
                  }}
                >
                  <i className="bi bi-info-circle text-danger opacity-75"></i>
                  <p className="m-0 text-light fs-6">
                    Select photos to{" "}
                    <span className="fw-medium text-light">restore</span> them
                    to the gallery, or{" "}
                    <span className="fw-medium text-danger">
                      permanently delete
                    </span>{" "}
                    them.
                  </p>
                </div>
              )}

              {/* ════════════ IMAGE GRID ════════════ */}
              <div
                className={`mt-1 ${isSelectMode ? "mb-8 pb-8" : "mb-8 pb-5"}`}
              >
                <div className="row g-2 mb-5">
                  {loading ? (
                    <div className="d-flex justify-content-center align-items-center py-5">
                      <div
                        className="spinner-border text-light"
                        role="status"
                      ></div>
                    </div>
                  ) : filteredImages.length === 0 ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
                      <i
                        className="bi bi-trash3 text-light mb-3"
                        style={{ fontSize: 64, opacity: 0.15 }}
                      ></i>
                      <p className="text-light opacity-50 fs-5 m-0">
                        {searchTerm ? "No results found" : "Bin is empty"}
                      </p>
                      {!searchTerm && (
                        <p className="text-light opacity-30 fs-6 mt-1">
                          Photos you remove will appear here
                        </p>
                      )}
                    </div>
                  ) : (
                    filteredImages.map((img, index) => (
                      <div
                        key={img._id || index}
                        className="position-relative col-lg-2 col-md-4 col-6"
                      >
                        <div
                          className="position-relative"
                          onClick={() => {
                            if (isSelectMode) toggleImageSelect(img._id);
                          }}
                          style={{
                            cursor: isSelectMode ? "pointer" : "default",
                          }}
                        >
                          {/* Action buttons — hidden while selecting */}
                          {!isSelectMode && loadedImages[img._id] && (
                            <div className="position-absolute top-0 end-0 d-flex flex-column m-2 gap-1 z-2">
                              <button
                                type="button"
                                data-bs-toggle="modal"
                                data-bs-target="#binActionModal"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openModal("restore", img._id);
                                }}
                                className="btn btn-white rounded-circle delete-button-lg p-0"
                                title="Restore"
                              >
                                <i className="bi bi-arrow-counterclockwise sm-icon text-black"></i>
                              </button>
                              <button
                                type="button"
                                data-bs-toggle="modal"
                                data-bs-target="#binActionModal"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openModal("delete", img._id);
                                }}
                                className="btn btn-white rounded-circle delete-button-lg p-0"
                                title="Delete forever"
                              >
                                <i className="bi bi-trash sm-icon text-black"></i>
                              </button>
                              <button
                                className="btn btn-white rounded-circle delete-button-lg p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSingleDownload(
                                    img.imageUrl,
                                    `image_${index + 1}.jpg`,
                                  );
                                }}
                                title="Download"
                              >
                                <i className="bi bi-download sm-icon text-black"></i>
                              </button>
                            </div>
                          )}

                          {isSelectMode ? (
                            <img
                              src={img.thumbnailUrl}
                              data-full={img.thumbnailUrl}
                              className="w-100 rounded-4 object-fit-cover"
                              height="170"
                              loading="lazy"
                              draggable={false}
                              style={{
                                opacity: loadedImages[img._id] ? 1 : 0,
                                transition: "opacity 0.3s ease",
                                filter: selectedImages.includes(img._id)
                                  ? "brightness(0.6)"
                                  : "grayscale(15%)",
                              }}
                              onLoad={(e) => {
                                const full = new Image();
                                full.src = e.target.dataset.full;
                                full.onload = () => {
                                  e.target.src = full.src;
                                  setLoadedImages((p) => ({
                                    ...p,
                                    [img._id]: true,
                                  }));
                                };
                              }}
                              alt="binned photo"
                            />
                          ) : (
                            <a href={img.imageUrl} className="glightbox-bin">
                              <img
                                src={img.thumbnailUrl}
                                data-full={img.thumbnailUrl}
                                className="w-100 rounded-4 object-fit-cover"
                                height="170"
                                loading="lazy"
                                draggable={false}
                                style={{
                                  opacity: loadedImages[img._id] ? 1 : 0,
                                  transition: "opacity 0.3s ease",
                                  filter: "grayscale(15%)",
                                }}
                                onLoad={(e) => {
                                  const full = new Image();
                                  full.src = e.target.dataset.full;
                                  full.onload = () => {
                                    e.target.src = full.src;
                                    setLoadedImages((p) => ({
                                      ...p,
                                      [img._id]: true,
                                    }));
                                  };
                                }}
                                alt="binned photo"
                              />
                            </a>
                          )}

                          {isSelectMode && selectedImages.includes(img._id) && (
                            <div
                              className="position-absolute top-50 start-50 translate-middle z-2"
                              style={{ pointerEvents: "none" }}
                            >
                              <i
                                className="bi bi-check-circle-fill text-white"
                                style={{
                                  fontSize: 34,
                                  filter:
                                    "drop-shadow(0 1px 3px rgba(0,0,0,0.5))",
                                }}
                              ></i>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* ── Multi-select bottom bar ── */}
              {isSelectMode && (
                <div className="fixed-bottom container w-lg-50 w-100 mt-7 mb-8 pb-3 d-flex">
                  <div className="bg-white py-2 ps-5 rounded-4 col-12 overflow-hidden">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <span
                          onClick={toggleSelectMode}
                          className="fs-4 rounded-3 me-3 cursor-pointer"
                        >
                          <i className="bi bi-x-lg"></i>
                        </span>
                        <h5 className="m-0 fw-normal">
                          Selected ({selectedImages.length}/{images.length})
                        </h5>
                      </div>
                      <div className="d-flex align-items-center gap-1">
                        {/* Download */}
                        <span
                          className="fs-6 rounded-3 me-1 circle-md-btn rounded-circle cursor-pointer btn btn-secondary"
                          onClick={
                            selectedImages.length > 0
                              ? handleMultiDownload
                              : undefined
                          }
                        >
                          <i className="bi bi-download text-white"></i>
                        </span>
                        {/* Restore selected */}
                        <span
                          className={`fs-6 rounded-3 me-1 circle-md-btn rounded-circle cursor-pointer btn btn-primary ${selectedImages.length === 0 ? "pe-none opacity-50" : ""}`}
                          data-bs-toggle={
                            selectedImages.length > 0 ? "modal" : undefined
                          }
                          data-bs-target={
                            selectedImages.length > 0
                              ? "#binActionModal"
                              : undefined
                          }
                          onClick={() =>
                            selectedImages.length > 0 &&
                            setActionMode("restore")
                          }
                          title="Restore selected"
                        >
                          <i className="bi bi-arrow-counterclockwise text-white"></i>
                        </span>
                        {/* Delete selected permanently */}
                        <span
                          className={`fs-6 rounded-3 me-1 circle-md-btn rounded-circle cursor-pointer btn btn-danger ${selectedImages.length === 0 ? "pe-none opacity-50" : ""}`}
                          data-bs-toggle={
                            selectedImages.length > 0 ? "modal" : undefined
                          }
                          data-bs-target={
                            selectedImages.length > 0
                              ? "#binActionModal"
                              : undefined
                          }
                          onClick={() =>
                            selectedImages.length > 0 && setActionMode("delete")
                          }
                          title="Delete forever"
                        >
                          <i className="bi bi-trash2 text-white"></i>
                        </span>
                        <span
                          onClick={toggleSelectAll}
                          className="btn btn-primary ps-4 pe-7 fs-6 rounded-start-pill"
                        >
                          {selectedImages.length === images.length
                            ? "De-Select"
                            : "Select"}{" "}
                          All
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* ════════ BIN ACTION MODAL (Restore or Delete Forever) ════════ */}
      <div
        className="modal fade"
        id="binActionModal"
        tabIndex="-1"
        aria-labelledby="binActionModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content rounded-4">
            <div className="modal-body py-0 ps-5">
              <div className="d-flex justify-content-end mt-n8">
                {actionMode === "restore" ? (
                  <div
                    className="d-flex align-items-center justify-content-center bg-white rounded-circle me-3 mt-3"
                    style={{ width: 110, height: 110 }}
                  >
                    <i
                      className="bi bi-arrow-counterclockwise text-primary"
                      style={{ fontSize: 52 }}
                    ></i>
                  </div>
                ) : (
                  <img
                    src="/assets/images/trash.png"
                    width="140"
                    alt="Trash Icon"
                  />
                )}
              </div>
              <div className="mt-n7">
                <h4 className="ls-sm mb-3" id="binActionModalLabel">
                  {actionMode === "restore" ? (
                    <>
                      <i className="bi bi-arrow-counterclockwise"></i> &nbsp;
                      {modalTitle}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash3"></i> &nbsp;{modalTitle}
                    </>
                  )}
                </h4>
                <p className="fs-5 lh-sm text-dark mb-1">{modalBody}</p>
                <p className="fs-6 lh-sm mt-2 text-secondary">
                  {actionMode === "delete" ? (
                    <>
                      {modalSubtext.split("gone forever")[0]}
                      <span className="fw-medium text-danger">
                        gone forever
                      </span>
                      .
                    </>
                  ) : (
                    modalSubtext
                  )}
                </p>
                <button
                  onClick={handleAction}
                  className={`btn border-0 px-4 rounded-3 fs-6 fw-medium mt-3 ${
                    actionMode === "restore" ? "btn-dark-primary" : "btn-danger"
                  }`}
                  disabled={isActionLoading}
                >
                  {isActionLoading ? (
                    <div
                      className="spinner-border spinner-border-sm text-light"
                      role="status"
                    ></div>
                  ) : actionMode === "restore" ? (
                    <>
                      <i className="bi bi-arrow-counterclockwise me-1"></i>{" "}
                      Restore
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash3 me-1"></i> Delete Forever
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="modal-footer border-0 p-0">
              <button
                type="button"
                className="btn btn-success rounded-circle px-3 label-text text-white btn-close-modal me-n3 mb-n2"
                data-bs-dismiss="modal"
                onClick={() => setSelectedImageId(null)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Bin;
